import { EditorContent } from "@tiptap/react";
import React, { useCallback } from "react";
import { SkillMarkdownLinkPreviewLayer } from "@promptlayer/ui";

import { useSkillCollectionEditorStore } from "../store";
import type { SkillDocumentJson } from "../utils/skill-markdown-convert";

import { SkillCollectionFrontmatterPanel } from "./SkillCollectionFrontmatterPanel";
import { SkillCollectionInlineLinkMenu } from "./SkillCollectionInlineLinkMenu";
import { useMarkdownEditorSync } from "./hooks/useMarkdownEditorSync";

import "@promptlayer/ui/skills/skill-markdown-editor.css";

interface MarkdownEditorProps {
  documentJson: SkillDocumentJson | undefined;
  fallbackMarkdown: string;
  hasImportedContent?: boolean;
  onClearImportFlag?: () => void;
  onDocumentChange: (doc: SkillDocumentJson) => void;
  onEditorReady?: (doc: SkillDocumentJson) => void;
  className?: string;
}

export const MarkdownEditor = ({
  documentJson,
  fallbackMarkdown,
  hasImportedContent,
  onClearImportFlag,
  onDocumentChange,
  onEditorReady,
  className,
}: MarkdownEditorProps) => {
  const store = useSkillCollectionEditorStore();
  const { editor } = useMarkdownEditorSync({
    documentJson,
    fallbackMarkdown,
    hasImportedContent,
    onClearImportFlag,
    onDocumentChange,
    onEditorReady,
    className,
  });

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const anchorElement = (event.target as HTMLElement).closest("a");
      if (anchorElement) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (!editor?.isEditable || editor.isDestroyed) {
        return;
      }

      const interactiveElement = (event.target as HTMLElement).closest(
        "button, input, textarea, select, label, [role='button']",
      );
      if (interactiveElement) {
        return;
      }

      editor.commands.focus();
    },
    [editor],
  );

  if (!editor) {
    return null;
  }

  return (
    <div className="relative flex h-full min-h-0 flex-col">
      <SkillCollectionInlineLinkMenu editor={editor} />
      <div className="flex h-full min-h-0 flex-col overflow-auto">
        <SkillCollectionFrontmatterPanel
          frontmatter={documentJson?.frontmatter ?? null}
          editable
          onChange={nextFrontmatter => {
            onDocumentChange({
              frontmatter: nextFrontmatter,
              bodyJson: editor.getJSON(),
            });
          }}
        />
        <SkillMarkdownLinkPreviewLayer
          filePath={store.selectedPath}
          hasLinkedFile={(path: string) => store.filePaths.has(path)}
          getLinkedFileContent={(path: string) => store.contentByPath.get(path)}
        >
          <div className="min-h-full flex-1 cursor-text" onClick={handleClick}>
            <EditorContent editor={editor} className="min-h-full flex-1 cursor-text" />
          </div>
        </SkillMarkdownLinkPreviewLayer>
      </div>
    </div>
  );
};
